/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { css } from '@emotion/css';
import React, { MouseEvent, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { ReactComponent as Balloon } from '../../assets/images/balloon.svg';
import { sendEvent } from '../../lib/google-analytics';
import { replaceAllSpaces, throttle } from '../../lib/utils';
import { FriendItem } from '../../models/FriendItem';
import { CharacterImage } from '../shared/CharacterImage';
import { IslandName } from '../shared/IslandName';

type FriendListItemProps = FriendItem;

export const FriendListItem: React.FC<FriendListItemProps> = ({
  id,
  name,
  image,
  landName,
  landDisplayId,
  isOnline,
  isReported
}) => {
  const { t, i18n } = useTranslation(['best_friends', 'translation']);
  const history = useHistory();

  // stopPropagationはthrottleされて欲しくないので，
  // 別のハンドラ関数に分けて定義
  const moveToFriendMessage = useCallback(
    throttle(() => {
      history.push(`/players/best-friends-message/${id}`);
    }, 1000),
    [id, history]
  );

  const handleCellClick = () => {
    history.push(`/players/best-friends/detail/${id}`);
    sendEvent('best_friends_top_player', 'click');
  };

  const handleButtonClick = (ev: MouseEvent) => {
    ev.stopPropagation();
    moveToFriendMessage();
    sendEvent('best_friends_top_message_to_person', 'click');
  };

  return (
    <li
      className={css`
        position: relative;
        padding: 2px 0 0;
        list-style-type: none;
        &:nth-child(n + 2)::after {
          content: '';
          position: absolute;
          top: 0;
          right: 24px;
          left: 88px;
          border-bottom: 2px dashed #dcd8bf;
        }
      `}
    >
      <button
        className={css`
          width: 100%;
          padding: 0 20px;
          border-radius: 2px;
          transition: background 0.1s;
          &:active {
            background: #ece5d4;
          }
          .content {
            display: flex;
            align-items: center;
            height: 80px;
            overflow: hidden;
          }
          .image {
            flex: 0 0 auto;
            display: flex;
            justify-content: center;
            align-items: center;
            width: 56px;
            height: 56px;
            background: #dcd8bf;
            margin: 0 12px 0 0;
            border-radius: 50%;
            position: relative;
          }
          .image img {
            border-radius: 50%;
            overflow: hidden;
          }
          .image::after {
            content: '';
            position: absolute;
            top: -2px;
            left: -2px;
            width: 19px;
            height: 19px;
            background: ${isOnline ? '#50b163' : '#707070'};
            border: 4px solid #f5f1e3;
            border-radius: 50%;
            transition: border 0.1s;
            /* workaround for iOS 13.3.1  */
            z-index: 1;
            transform: translate3d(0, 0, 1px);
          }
          :active .image::after {
            border: 4px solid #ece5d4;
          }
          .names {
            flex: 1 1 auto;
            display: flex;
            flex-direction: column;
            margin-right: ${isOnline ? '52px' : '12px'};
            overflow: hidden;
            white-space: nowrap;
          }
          .island {
            font-size: 12px;
            color: #938666;
          }
          .name {
            color: #493a31;
          }
          .name,
          .island {
            width: 100%;
            overflow: hidden;
            white-space: nowrap;
            text-overflow: ellipsis;
            text-align: left;
            text-align: start;
          }
          .name {
            font-size: 16px;
          }
          .island {
            font-size: 14px;
          }
        `}
        onClick={handleCellClick}
        aria-labelledby={`names-${id} online-status-${id}`}
      >
        <div className="content">
          <div
            id={`online-status-${id}`}
            className={`image ${isOnline ? 'online' : ''}`}
            aria-label={`${isOnline ? t('online') : t('offline')}`}
            aria-hidden={true}
          >
            <CharacterImage src={image} isPlayer={true} isReported={isReported} ariaHidden={true} />
          </div>
          <div className="names" id={`names-${id}`}>
            {isReported ? (
              <span className="name">{t('translation:reported_player')}</span>
            ) : (
              <>
                <span className="island">
                  <IslandName
                    landName={landName}
                    landDisplayId={landDisplayId}
                    language={i18n.language}
                  />
                </span>
                <span className="name">{replaceAllSpaces(name)}</span>
              </>
            )}
          </div>
        </div>
      </button>
      {isOnline && (
        <div
          className={css`
            position: absolute;
            z-index: 1;
            height: 100%;
            top: 2px;
            right: 20px;
            display: flex;
            flex-direction: column;
            justify-content: center;

            .balloon {
              flex: 0 0 auto;
              width: 44px;
              height: 44px;
              background: #fb984a;
              padding: 0;
              border-radius: 50%;
              transition: transform 0.15s cubic-bezier(0.17, 0.89, 0.32, 1.49);
              &:active {
                transform: scale(0.85);
              }
            }
            .balloon-icon {
              display: flex;
              justify-content: center;
              align-items: center;
            }
          `}
        >
          <button
            className="balloon"
            onClickCapture={handleButtonClick}
            aria-label={t('aria_message_to_person')}
          >
            <div className="balloon-icon">
              <Balloon />
            </div>
          </button>
        </div>
      )}
    </li>
  );
};
